/**
 * Cosmetics v3 hero slider: vanilla custom element, no dependencies.
 *
 * Handles autoplay (pausable), previous and next arrows, dots, keyboard
 * arrows, pointer and touch swipe, RTL direction, and reduced motion.
 * Each instance cleans up its listeners on disconnect, so Theme Editor
 * section reloads never double-initialize.
 */
const SWIPE_THRESHOLD = 40;

class Cv3HeroSlider extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.track = this.querySelector('[data-cv3-hero-track]');
    this.slides = [...this.querySelectorAll('[data-cv3-hero-slide]')];
    this.dots = [...this.querySelectorAll('[data-cv3-hero-dot]')];
    this.prev = this.querySelector('[data-cv3-hero-prev]');
    this.next = this.querySelector('[data-cv3-hero-next]');
    this.pauseButton = this.querySelector('[data-cv3-hero-pause]');
    this.viewport = this.querySelector('[data-cv3-hero-viewport]');

    if (!this.track || this.slides.length === 0) return;

    this.index = 0;
    this.timer = null;
    this.userPaused = false;
    this.hovering = false;
    this.focused = false;
    this.interacted = false;
    this.dragStart = null;
    this.total = this.slides.length;
    this.delay = Number(this.dataset.delay) || 3000;
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.total > 1;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isRtl = document.documentElement.dir === 'rtl';
    this.direction = this.isRtl ? -1 : 1;

    if (this.reducedMotion) {
      this.autoplayEnabled = false;
      this.setAttribute('data-reduced-motion', 'true');
    }

    this.prev?.addEventListener('click', () => this.go(this.index - 1, true), { signal });
    this.next?.addEventListener('click', () => this.go(this.index + 1, true), { signal });
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true), { signal }));
    this.pauseButton?.addEventListener('click', () => this.togglePause(), { signal });

    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.addEventListener('focusin', () => { this.focused = true; this.stopTimer(); }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) { this.focused = false; this.scheduleIfAllowed(); }
    }, { signal });
    this.addEventListener('pointerenter', () => { this.hovering = true; this.stopTimer(); }, { signal });
    this.addEventListener('pointerleave', () => { this.hovering = false; this.scheduleIfAllowed(); }, { signal });

    this.viewport?.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
    this.viewport?.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
    this.viewport?.addEventListener('pointercancel', () => { this.dragStart = null; }, { signal });

    this.render();
    this.scheduleIfAllowed();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.stopTimer();
  }

  go(target, byUser = false) {
    if (byUser) {
      this.interacted = true;
      this.stopTimer();
    }
    const count = this.total;
    this.index = ((target % count) + count) % count;
    this.render();
    if (!byUser) this.scheduleIfAllowed();
  }

  render() {
    const offset = -this.index * 100 * this.direction;
    this.track.style.transform = `translate3d(${offset}%, 0, 0)`;

    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.toggleAttribute('inert', !active);
      const link = slide.querySelector('a');
      if (link) link.tabIndex = active ? 0 : -1;
    });

    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.setAttribute('aria-current', active ? 'true' : 'false');
      dot.classList.toggle('is-active', active);
    });
  }

  scheduleIfAllowed() {
    this.stopTimer();
    if (!this.autoplayEnabled || this.userPaused || this.interacted || this.hovering || this.focused) return;
    this.timer = window.setTimeout(() => this.go(this.index + 1), this.delay);
  }

  stopTimer() {
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = null;
  }

  togglePause() {
    this.userPaused = !this.userPaused;
    this.pauseButton?.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
    this.dataset.paused = this.userPaused ? 'true' : 'false';
    this.scheduleIfAllowed();
  }

  onKeydown(event) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      const forward = event.key === 'ArrowRight';
      // In RTL, the logical "next" direction is leftward.
      const step = (forward ? 1 : -1) * (this.isRtl ? -1 : 1);
      if (!this.contains(event.target) || event.target.closest('input, textarea, select')) return;
      event.preventDefault();
      this.go(this.index + step, true);
    }
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.dragStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
  }

  onPointerUp(event) {
    if (!this.dragStart) return;
    const dx = (event.clientX - this.dragStart.x) * this.direction;
    const dy = Math.abs(event.clientY - this.dragStart.y);
    this.dragStart = null;
    if (dy > Math.abs(dx)) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    // Negative dx means the finger moved toward the logical start, so advance.
    this.go(this.index + (dx < 0 ? 1 : -1), true);
  }
}

if (!customElements.get('cv3-hero-slider')) {
  customElements.define('cv3-hero-slider', Cv3HeroSlider);
}
